import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../components/navbar/navbar.component";
import { schemesV2Api } from "../../services/schemesV2Api";
import { generalToastStyle } from "../../utils/toast.styles";
import PaymentScheduleTable from "./PaymentScheduleTable";

const SchemesV2Main = () => {
  const { schemeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentEnrollment, setCurrentEnrollment] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  
  // Get enrollment data from navigation state
  const enrollmentFromState = location.state?.enrollment;


  // Helper function to get terms URL
  const getTermsUrl = (schemeId) => {
    switch (schemeId) {
      case "11-month-direct-purchase":
        return {
          url: "https://blogs.sadashrijewelkart.com/key-terms-11-month-direct-purchase-plan/",
          title: "11-Month Direct Purchase Plan Terms"
        };
      case "10-month-accumulation":
        return {
          url: "https://blogs.sadashrijewelkart.com/key-terms-10-month-accumulation/",
          title: "10-Month Accumulation Terms"
        };
      case "30-day-daily-savings":
        return {
          url: "https://blogs.sadashrijewelkart.com/turn-daily-savings-into-timeless-treasures/",
          title: "Daily Savings Terms"
        };
      default:
        return {
          url: "https://blogs.sadashrijewelkart.com/key-terms-10-month-accumulation/",
          title: "Terms and Conditions"
        };
    }
  };



  // Generate payment schedule based on enrollment data
  const generatePaymentSchedule = (enrollment) => {
    if (!enrollment) {
      return [];
    }
    
    const schedule = [];
    const enrollmentDate = new Date(enrollment.enrollment_date);
    const installmentAmount = parseFloat(enrollment.instalment_amount || 0);
    const totalPeriods = enrollment.plan_progress?.total_periods || 0;
    const periodsPaid = enrollment.plan_progress?.periods_paid || 0;
    
    // Get payment history if available - check multiple possible locations
    const paymentHistory = enrollment.payment_details?.payment_history || 
                          enrollment.paymentHistory || 
                          enrollment.payment_history || 
                          enrollment.payment_details || [];
    
    console.log("paymentHistory", paymentHistory);
    
    for (let i = 1; i <= totalPeriods; i++) {
      // Find payment record for this installment
      const payment = paymentHistory.find(p => 
        parseInt(p.installment_number) === i
      );
      
      let status = 'pending';
      let paymentDate = null;
      let goldRateOnPaymentDate = null;
      let goldGramsCredited = null;
      
      if (payment) {
        // Use the actual due_date from payment history instead of calculating
        const dueDate = new Date(payment.due_date);
        
        // Determine status based on payment data
        if (payment.status === 'paid') {
          status = 'paid';
          // Convert UTC updated_at to IST for payment date
          if (payment.updated_at) {
            const utcDate = new Date(payment.updated_at);
            const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000)); // Add 5.5 hours for IST
            paymentDate = istDate.toISOString().replace('T', ' ').substring(0, 19);
          }
          goldRateOnPaymentDate = payment.gold_rate_on_payment;
          goldGramsCredited = payment.gold_grams_credited;
        } else if (payment.status === 'pending' && i === periodsPaid + 1) {
          status = 'next';
        }
        
        schedule.push({
          installmentNumber: i,
          dueDate: dueDate.toLocaleDateString('en-GB'),
          paymentDate: paymentDate,
          amount: parseFloat(payment.amount),
          status: status,
          goldRate: goldRateOnPaymentDate,
          goldGramsCredited: goldGramsCredited,
          transactionId: payment.transaction_id,
          paymentMethod: payment.payment_method,
          updatedAt: payment.updated_at
        });
      } else {
        // Fallback: If no payment record found, calculate due date
        const dueDate = new Date(enrollmentDate);
        dueDate.setMonth(dueDate.getMonth() + i - 1);
        
        if (i <= periodsPaid) {
          status = 'paid';
        } else if (i === periodsPaid + 1) {
          status = 'next';
        }
        
        schedule.push({
          installmentNumber: i,
          dueDate: dueDate.toLocaleDateString('en-GB'),
          paymentDate: paymentDate,
          amount: installmentAmount,
          status: status,
          goldRate: goldRateOnPaymentDate,
          goldGramsCredited: goldGramsCredited
        });
      }
    }
    
    console.log("schedule", schedule);
    return schedule;
  };

  // Update payment schedule when currentEnrollment changes
  useEffect(() => {
    if (currentEnrollment) {
      const schedule = generatePaymentSchedule(currentEnrollment);
      setPaymentSchedule(schedule);
    } else {
      setPaymentSchedule([]);
    }
  }, [currentEnrollment]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch schemes
        const schemesResponse = await schemesV2Api.getAllSchemes();
        setSchemes(schemesResponse.data.schemes);

        // Set selected scheme
        if (schemeId) {
          const scheme = schemesResponse.data.schemes.find(s => s.id === schemeId);
          setSelectedScheme(scheme);
        } else {
          setSelectedScheme(schemesResponse.data.schemes[0]);
        }

        // Fetch user enrollments if user is logged in
        const userId = localStorage.getItem('user_id');
        if (userId) {
          try {
            const enrollmentsResponse = await schemesV2Api.getUserSchemes(userId);
            const activeSchemes = enrollmentsResponse.data?.activeSchemes || [];
            const completedSchemes = enrollmentsResponse.data?.completedSchemes || [];
            const allEnrollments = [...activeSchemes, ...completedSchemes];
            setUserEnrollments(allEnrollments);
          } catch (enrollmentError) {
            console.warn('Failed to fetch user enrollments:', enrollmentError.message);
          }
        }
      } catch (err) {
        setError(err.message);
        toast.error(`Failed to load schemes: ${err.message}`, generalToastStyle);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [schemeId]);

  // Update current enrollment when selected scheme or user enrollments change
  useEffect(() => {
    console.log("userEnrollments", userEnrollments);
    // If we have enrollment data from navigation state, use it
    if (enrollmentFromState) {
      setCurrentEnrollment(enrollmentFromState);
    } else if (selectedScheme && userEnrollments.length > 0) {
      const enrollment = userEnrollments.find(
        enrollment => enrollment.scheme_id === selectedScheme.id
      );
      console.log("enrollment", enrollment);
      setCurrentEnrollment(enrollment || null);
    } else {
      setCurrentEnrollment(null);
    }
  }, [selectedScheme, userEnrollments, enrollmentFromState]);

  // Handle loading state
  if (isLoading) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ color: "#A36E29", mb: 2 }} 
          />
          <Typography variant="h6" sx={{ color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
            Loading schemes...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Typography variant="h6" sx={{ color: "#d32f2f", mb: 2 }}>
            Error loading schemes
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ backgroundColor: "#A36E29" }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  if (!selectedScheme) return null;




  // Get scheme-specific FAQs
  const getSchemeFAQs = (schemeId) => {
    const faqData = {
      '11-month-direct-purchase': [
        {
          question: "What's the quick summary?",
          answer: "Pay monthly for 11 months; on completion we waive 100% of the making/setting charge on one invoice (wastage/metal/stone/taxes still payable)."
        },
        {
          question: "Who is this best for?",
          answer: "Shoppers who want predictable savings with a clear, purchase-time making-charge benefit."
        },
        {
          question: "How does it work, step-by-step?",
          answer: "Choose a fixed monthly instalment (₹1,000+), pay for 11 months (5-day grace each month); on redemption the making/setting charge on one invoice is waived per T&Cs."
        },
        {
          question: "What's the tenure and grace?",
          answer: "11 months; 5-day grace after each due date."
        },
        {
          question: "Can I change my monthly amount later?",
          answer: "No—fixed once selected."
        },
        {
          question: "What payment modes are allowed?",
          answer: "UPI / net-banking; autopay recommended; mandates per network rules."
        },
        {
          question: "Exactly what gets waived?",
          answer: "100% of the making/setting component on one invoice; wastage, metal/stone value, hallmark/certification, duties and taxes remain payable."
        },
        {
          question: "Are \"no-making\" and \"price discount\" the same?",
          answer: "No—the waiver removes the making line; it's not a price cut on metal/stone."
        },
        {
          question: "When and how do I redeem?",
          answer: "After paying all 11 instalments; single invoice within the store-specified redemption period (e.g., 30 days)."
        },
        {
          question: "What can I buy (eligible categories)?",
          answer: "Gold (14K/18K/22K), diamond & gemstone-studded jewellery, silver articles, bespoke orders, gold coins (subject to policy)."
        },
        {
          question: "What is excluded?",
          answer: "Gift cards/credits, items above store threshold, services/repairs unless allowed."
        },
        {
          question: "Any price locks or rate promises?",
          answer: "No price locks unless offered in writing; invoice follows redemption-day rates (pre-waiver). Any \"14% making\" shown is illustrative only."
        },
        {
          question: "Can I redeem early—and what happens then?",
          answer: "Early redemption allowed but full 100% waiver needs all 11 payments; early-exit slabs may apply."
        },
        {
          question: "What are the early-exit slabs (illustrative)?",
          answer: "1–5 months: 0% making; 6–8: 25% of making; 9–11: 50% of making; full 11: 100% making waiver."
        },
        {
          question: "Can I split the waiver across invoices?",
          answer: "No—single invoice only; not encashable/transferable."
        },
        {
          question: "Can I combine this with other offers?",
          answer: "Generally yes unless a specific offer disallows; store may limit stacking."
        },
        {
          question: "Can I run multiple plans?",
          answer: "Yes; benefit applies to one invoice and can't be split; only one scheme benefit per invoice unless store policy allows otherwise."
        },
        {
          question: "Who can enrol and what KYC is needed?",
          answer: "Indian residents 18+; KYC per law/policy; PAN where required; individuals only unless expressly permitted."
        },
        {
          question: "Records, privacy and DPDP compliance?",
          answer: "Store e-records are valid; DPDP consent per Store Privacy Notice."
        },
        {
          question: "What if I miss a month?",
          answer: "Pay within the 5-day grace to stay on track; outside grace, the plan continues but you may fall into early-exit slabs."
        },
        {
          question: "Payments, limits and AML?",
          answer: "Cash transactions (if accepted) follow Section 269ST/other limits; store may decline payments breaching rules; AML/KYC compliance required."
        },
        {
          question: "Disputes and governing law?",
          answer: "Laws of India; exclusive jurisdiction as notified (e.g., Bengaluru); escalation path provided."
        },
        {
          question: "Disclaimers & liability limits?",
          answer: "Promotional purchase-assist (not deposit/investment); liability limited; illustrations are indicative only."
        }
      ],
      '10-month-accumulation': [
        {
          question: "What's the quick summary?",
          answer: "Pay monthly for 10 months; each payment converts to gold/silver grams at that day's rate; on completion get a bonus = 50% of one monthly instalment."
        },
        {
          question: "Who is this best for?",
          answer: "Shoppers who prefer market-linked accumulation with a clear completion bonus."
        },
        {
          question: "How does it work, step-by-step?",
          answer: "Choose monthly amount (₹1,000+), grams credited each month at store-published 22K/999 rate; at delivery your grams are valued at delivery-day rate + completion bonus added."
        },
        {
          question: "What's the tenure and due-date policy?",
          answer: "Tenure 10 months; a 5-day grace follows each monthly due date."
        },
        {
          question: "Can I change my monthly amount later?",
          answer: "No—fixed for the plan once chosen."
        },
        {
          question: "What payment modes are allowed?",
          answer: "UPI/net-banking; autopay recommended; card/UPI mandates per network rules."
        },
        {
          question: "How are grams calculated and posted?",
          answer: "Grams = instalment ÷ same-day metal rate; fractional up to three decimals; rate per store-published source."
        },
        {
          question: "What if I miss a month?",
          answer: "If unpaid beyond the grace window, no grams credited for that month."
        },
        {
          question: "What completion bonus do I get and when?",
          answer: "On 10 posted monthly payments, bonus = 50% of one monthly instalment; applied before taxes on a single invoice."
        },
        {
          question: "Can I redeem early?",
          answer: "Yes, you can redeem grams anytime before Month 10 but no completion bonus; early-exit slabs may give making-charge discounts."
        },
        {
          question: "What's the redemption window and pricing basis at delivery?",
          answer: "Redeem within 30 days after Month 10; final invoice uses delivery-day metal rate, making/setting, stones, certification and taxes."
        },
        {
          question: "What can I buy (eligible categories)?",
          answer: "Diamond & gemstone-studded gold, plain gold (14K/18K/22K), silver articles/collections, bespoke orders, gold coins (subject to policy)."
        },
        {
          question: "What is excluded?",
          answer: "Gift cards/credits, items above store threshold, services/repairs unless allowed."
        },
        {
          question: "Are there price locks? What about taxes/levies?",
          answer: "No locks; delivery-day rates and statutory taxes/levies apply."
        },
        {
          question: "Can I split redemption across invoices or carry forward bonus?",
          answer: "No—single invoice only; bonus isn't encashable/transferable/carry-forwardable beyond the window."
        },
        {
          question: "Early-exit benefit slabs (illustrative)?",
          answer: "Months 1–5: 0% making; 6–8: 15% of making; 9–10: 25% of making; full 10: 50% of one instalment as bonus."
        },
        {
          question: "Combining with other offers?",
          answer: "Generally allowed unless a specific offer disallows; store may limit stacking."
        },
        {
          question: "Can I run multiple plans?",
          answer: "Yes; each plan's bonus (if any) applies to one invoice only."
        },
        {
          question: "Who can enrol and what KYC is needed?",
          answer: "Indian residents 18+; KYC as per law/policy; PAN where legally required; individuals only unless expressly permitted."
        },
        {
          question: "Data, records and privacy?",
          answer: "Store e-records are valid; DPDP consent per Store Privacy Notice."
        },
        {
          question: "Disputes and governing law?",
          answer: "Laws of India; exclusive jurisdiction as notified (e.g., Bengaluru); escalation path defined."
        },
        {
          question: "Payment limits, AML/Compliance?",
          answer: "Cash transactions (if accepted) follow legal limits; AML/KYC compliance required; store may decline if thresholds exceeded."
        },
        {
          question: "Disclaimers & liability limits?",
          answer: "Promotional savings in metal grams (not deposit/investment); liability limited; illustrations are indicative."
        }
      ],
      '30-day-daily-savings': [
        {
          question: "What's the quick summary?",
          answer: "Pay daily for 30 days; on completion you get a store bonus equal to 100% of one day's instalment, usable on one invoice."
        },
        {
          question: "Who is this best for?",
          answer: "Shoppers wanting a short, disciplined, pre-purchase plan for gems & jewellery."
        },
        {
          question: "How does it work, step-by-step?",
          answer: "Pick a daily amount (₹100–₹5,000+), pay for 30 days (≥28 required), bonus (one day's instalment) is added at completion; redeem within the window."
        },
        {
          question: "What's the tenure and minimum to qualify?",
          answer: "Tenure is 30 calendar days; qualify with at least 28 paid days."
        },
        {
          question: "How much can I pay each day? Can I change it later?",
          answer: "Any whole amount ₹100–₹5,000 or higher as permitted; once chosen, it can't be changed for that plan window."
        },
        {
          question: "What payment modes are allowed?",
          answer: "UPI / net-banking; autopay recommended. Card/UPI mandates subject to network rules."
        },
        {
          question: "What if I miss a day or autopay fails?",
          answer: "Up to two non-consecutive misses allowed if you still complete ≥28 days. Repeated autopay failures may disable the mandate and affect qualification."
        },
        {
          question: "Exactly what bonus do I get and when?",
          answer: "On 30/30 days, a Store Bonus equal to 100% of one day's instalment is credited for redemption."
        },
        {
          question: "Can I redeem early?",
          answer: "Manager-level early redemption may be allowed at Day 29; terms computed on actual days paid/top-up."
        },
        {
          question: "Where and when do I redeem?",
          answer: "Once, via a single invoice, within the 15-day redemption window after your last valid payment."
        },
        {
          question: "What can I use it on (eligible categories)?",
          answer: "Diamond & gemstone-studded gold, plain gold (14K/18K/22K), gold coins, silver articles & jewellery, ready-to-ship designs."
        },
        {
          question: "What is excluded?",
          answer: "Gift cards/credits, loose solitaires above store threshold, and services/repairs unless allowed."
        },
        {
          question: "Any price locks? How is pricing decided?",
          answer: "No price locks; the final invoice uses billing-day gold rate, making/setting, certification and taxes."
        },
        {
          question: "How are taxes/levies handled?",
          answer: "GST, hallmark/certification and other statutory levies apply; TCS/TDS collected if applicable."
        },
        {
          question: "Can I cash out the bonus or split it across invoices?",
          answer: "No cashout or split billing; bonus applies on one invoice only and unused value is forfeited."
        },
        {
          question: "What if I stop early?",
          answer: "<28 days: full principal refund to original mode, no interest/bonus, typically within 5–7 working days."
        },
        {
          question: "Can I combine this with other offers?",
          answer: "Usually yes, unless a specific promo disallows stacking; store may limit clubbing."
        },
        {
          question: "Can I run multiple plans?",
          answer: "Yes, multiple plans per mobile are permitted; only one scheme bonus per invoice."
        },
        {
          question: "Are gold coins allowed? What about gift cards?",
          answer: "Gold coins are allowed; gift cards/credits are excluded."
        },
        {
          question: "What KYC and who can enrol?",
          answer: "Indian residents 18+ with valid ID; PAN where legally required (e.g., ≥₹50,000 invoice). Individuals only unless explicitly permitted."
        },
        {
          question: "Data, records and privacy?",
          answer: "Store e-records are valid evidence; DPDP Act consent applies per Store Privacy Notice."
        },
        {
          question: "Disputes and governing law?",
          answer: "Laws of India; Bengaluru jurisdiction; escalation path includes Store Manager and Grievance Officer."
        },
        {
          question: "Any disclaimers/limits of liability?",
          answer: "It's a promotional purchase-assist (not savings/deposit); liability limited to amounts received and applicable bonus. Illustrations are indicative."
        }
      ]
    };
    return faqData[schemeId] || [];
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <Navbar />
      
      <Box
        sx={{
          background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
          minHeight: "100vh",
          pt: { xs: 25, md: 12 },
          pb: 4
        }}
      >
        {/* Header Section */}
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              color: "#A36E29",
              fontWeight: 700,
              textAlign: "center",
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
              fontFamily: '"Roboto", sans-serif'
            }}
          >
            Customer Payment Schemes
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(163, 110, 41, 0.8)",
              textAlign: "center",
              fontWeight: 400,
              mb: 4,
              fontFamily: '"Roboto", sans-serif'
            }}
          >
            Plan, pay, and redeem for your favourite gems & jewels with clear due dates and benefits.
          </Typography>
        </Container>

        {/* Main Content Card */}
        <Container maxWidth="lg" sx={{ pb: 12 }}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              overflow: "hidden"
            }}
          >
            {/* Scheme Selection Header - Only show if no specific schemeId */}
            {console.log("schemeId",schemeId)}
            {schemeId && (
              <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                      Selected scheme
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {schemes.filter(scheme => scheme.id === schemeId).map((scheme) => (
                        <Button
                          key={scheme.id}
                          variant={selectedScheme.id === scheme.id ? "contained" : "outlined"}
                          size="small"
                          onClick={() => setSelectedScheme(scheme)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "12px",
                            fontFamily: '"Roboto", sans-serif',
                            backgroundColor: selectedScheme.id === scheme.id ? "#A36E29" : "transparent",
                            color: selectedScheme.id === scheme.id ? "white" : "#A36E29",
                            borderColor: "#A36E29",
                            "&:hover": {
                              backgroundColor: selectedScheme.id === scheme.id ? "#A36E29" : "rgba(163, 110, 41, 0.04)",
                              borderColor: "#A36E29"
                            }
                          }}
                        >
                          {scheme.name}
                        </Button>
                      ))}
                    </Box>
                  </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                    Enrolment date
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                    {currentEnrollment ? new Date(currentEnrollment.enrollment_date).toLocaleDateString() : 'Not enrolled'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                    {selectedScheme.dailyInstalment ? "Daily instalment" : "Monthly instalment"}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                    ₹{parseFloat(currentEnrollment?.instalment_amount || selectedScheme.monthlyInstalment || selectedScheme.dailyInstalment || 0).toLocaleString()}
                    {selectedScheme.dailyInstalment && <Typography component="span" variant="body2" sx={{ ml: 1, fontFamily: '"Roboto", sans-serif' }}>/day</Typography>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                    Fixed for the plan; you can open another plan for a new amount.
                  </Typography>
                </Grid>
              </Grid>

              {/* Current Enrollment Status */}
              {currentEnrollment ? (
                <Box sx={{ mt: 3, p: 2, backgroundColor: "white", borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                    Your enrollment progress
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={currentEnrollment.plan_progress?.percentage || 0}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "#f5f5f5",
                        "& .MuiLinearProgress-bar": {
                          background: "linear-gradient(to right, #d4a76a, #A36E29)"
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: "80px", fontFamily: '"Roboto", sans-serif' }}>
                      {currentEnrollment.plan_progress?.periods_paid || 0} / {currentEnrollment.plan_progress?.total_periods || 0} paid
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: '"Roboto", sans-serif' }}>
                    Status: <strong>{currentEnrollment.status?.toUpperCase()}</strong> • Total invested: ₹{parseFloat(currentEnrollment.plan_progress?.total_paid || 0).toLocaleString()}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 3, p: 2, backgroundColor: "rgba(163, 110, 41, 0.1)", borderRadius: 2, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                    Not enrolled in this scheme
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                    Click "Enroll now" below to start your savings journey
                  </Typography>
                </Box>
              )}
            </Box>
            )}

            <CardContent sx={{ p: 0 }}>
              <Grid container>
                {/* Left Column - Payment Details */}
                <Grid item xs={12} md={8} sx={{ p: 3 }}>
                  {/* Next Payment */}
                  {currentEnrollment ? (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                        Next payment
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Chip
                          icon={<CalendarTodayIcon />}
                          label={`Next installment due`}
                          color="info"
                          variant="outlined"
                          sx={{ fontFamily: '"Roboto", sans-serif' }}
                        />
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Amount
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            ₹{parseFloat(currentEnrollment.instalment_amount || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Grace: {selectedScheme.grace_periods_after_due || selectedScheme.gracePeriodsAfterDue || 0} days after due date
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          onClick={() => navigate(`/schemes-v2/payment/${currentEnrollment.id}`)}
                          sx={{
                            background: "linear-gradient(to right, #d4a76a, #A36E29)",
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            fontFamily: '"Roboto", sans-serif',
                            "&:hover": {
                              background: "linear-gradient(to right, #c18a3d, #8b5a1f)"
                            }
                          }}
                        >
                          Pay now
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 4, p: 3, backgroundColor: "rgba(163, 110, 41, 0.1)", borderRadius: 2, textAlign: "center" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                        Ready to start saving?
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: '"Roboto", sans-serif' }}>
                        Enroll in this scheme to begin your savings journey
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/schemes-v2/enroll/${selectedScheme.id}`)}
                        sx={{
                          background: "linear-gradient(to right, #d4a76a, #A36E29)",
                          borderRadius: 2,
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          fontFamily: '"Roboto", sans-serif',
                          "&:hover": {
                            background: "linear-gradient(to right, #c18a3d, #8b5a1f)"
                          }
                        }}
                      >
                        Enroll now
                      </Button>
                    </Box>
                  )}

                  {/* Plan Progress */}
                  {currentEnrollment ? (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                        Your progress
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: '"Roboto", sans-serif' }}>
                          {currentEnrollment.plan_progress?.percentage || 0}%
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={currentEnrollment.plan_progress?.percentage || 0}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "#f5f5f5",
                              "& .MuiLinearProgress-bar": {
                                background: "linear-gradient(to right, #d4a76a, #A36E29)"
                              }
                            }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: '"Roboto", sans-serif' }}>
                            {currentEnrollment.plan_progress?.periods_paid || 0} of {currentEnrollment.plan_progress?.total_periods || 0} installments paid
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Total invested
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            ₹{parseFloat(currentEnrollment.plan_progress?.total_paid || 0).toLocaleString()}
                          </Typography>
                        </Grid>
                        {selectedScheme?.id === '10-month-accumulation' && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                              {selectedScheme?.metal?.toLowerCase() === 'silver' ? 'Silver' : 'Gold'} grams earned
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                              {selectedScheme?.metal?.toLowerCase() === 'silver'
                                ? parseFloat(currentEnrollment.gold_grams_accumulated || 0).toFixed(2)
                                : parseFloat(currentEnrollment.gold_grams_accumulated || 0).toFixed(3)
                              }g
                            </Typography>
                          </Grid>
                        )}
                      </Grid>

                      {/* Completion Reward */}
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.1)",
                          borderRadius: 2,
                          textAlign: "center",
                          border: "1px solid rgba(163, 110, 41, 0.2)"
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                          Completion reward
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                          {selectedScheme.completion_reward || selectedScheme.completionReward || 'No reward specified'}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                        Scheme details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Total amount
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            ₹{(selectedScheme.totalAmount || 0).toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Tenure
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            {selectedScheme.tenure || selectedScheme.tenure_display || 'Not set'}
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* Completion Reward */}
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.1)",
                          borderRadius: 2,
                          textAlign: "center",
                          border: "1px solid rgba(163, 110, 41, 0.2)"
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                          Completion reward
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                          {selectedScheme.completion_reward || selectedScheme.completionReward || 'No reward specified'}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Payment Schedule */}
                  <Box sx={{ mb: 4 }}>
                    <PaymentScheduleTable 
                      paymentSchedule={paymentSchedule} 
                      currentEnrollment={currentEnrollment}
                      selectedScheme={selectedScheme}
                    />
                  </Box>
                </Grid>

                {/* Right Column - Key Terms & What This Buys */}
                <Grid item xs={12} md={4} sx={{ backgroundColor: "#f8f9fa", p: 3 }}>
                {/* FAQs Section */}
                <Card sx={{ mt: 3, borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, fontFamily: '"Roboto", sans-serif' }}>
                      FAQs
                    </Typography>
                    {(selectedScheme.faqs || getSchemeFAQs(selectedScheme?.id)).map((faq, index) => (
                    <Accordion key={index} sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                    <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>

                  {/* What This Typically Buys */}
                  {/* <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                      What this typically buys (examples)
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {(detailedSchemeData?.whatThisBuys || 
                        selectedScheme?.whatThisBuys || 
                        selectedScheme?.what_this_buys || 
                        getFallbackWhatThisBuys(selectedScheme?.id) ||
                        []).map((category, index) => (
                        <Box key={index}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontFamily: '"Roboto", sans-serif' }}>
                            {category.item || category.category}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            {category.description}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box> */}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Terms and Conditions Link */}
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography 
              variant="body2" 
              sx={{ fontFamily: '"Roboto", sans-serif', color: "text.secondary" }}
            >
              By proceeding, you agree to the{" "}
              <a
                href={getTermsUrl(selectedScheme?.id).url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#A36E29",
                  textDecoration: "underline",
                  fontWeight: 500
                }}
              >
                Terms and Conditions
              </a>
            </Typography>
          </Box>

          {/* Bottom Action Bar */}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(to right, #d4a76a, #A36E29)",
              p: 2,
              display: { xs: "none", md: "flex" },
              justifyContent: "space-between",
              alignItems: "center",
              color: "white",
              zIndex: 1000,
              fontFamily: '"Roboto", sans-serif'
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontFamily: '"Roboto", sans-serif' }}>You're viewing</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                {selectedScheme.name}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ flex: 1, textAlign: "center", fontFamily: '"Roboto", sans-serif' }}>
              {currentEnrollment ? 
                `Progress: ${currentEnrollment.plan_progress?.percentage || 0}% • Status: ${currentEnrollment.status}` :
                `Completion reward: ${selectedScheme.completion_reward || selectedScheme.completionReward || 'No reward specified'}`
              }
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                component="a"
                href={getTermsUrl(selectedScheme?.id).url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  borderColor: "white",
                  fontFamily: '"Roboto", sans-serif',
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)"
                  }
                }}
              >
                View Terms & Conditions
              </Button>
              {currentEnrollment ? (
                <Button
                  variant="contained"
                  onClick={() => navigate(`/schemes-v2/payment/${currentEnrollment.id}`)}
                  sx={{
                    backgroundColor: "white",
                    color: "#A36E29",
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)"
                    }
                  }}
                >
                  Make Payment
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => navigate(`/schemes-v2/enroll/${selectedScheme.id}`)}
                  sx={{
                    backgroundColor: "white",
                    color: "#A36E29",
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)"
                    }
                  }}
                >
                  Enroll now
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Container>
  );
};

export default SchemesV2Main;
